/**
 * Created by Arif Topcu in 2019
 */

import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Combobox, { Size } from "@ls/react-combobox-grid";
import Checkbox from "@ls/react-checkbox";
import FilterSummary from "./FilterSummary";
import FilterSelection from "./FilterSelection";
import BetaSelection from "./BetaSelection";
import {
  selectAnalyticValuesSelectionOptions,
  selectFetchingAnalyticValues,
} from "state/analytics/selectors";
import {
  selectFetchingGroupingLevels,
  selectGroupingLevelOptions,
} from "state/selections/selector";
import { summaryTypeOptionsLegacy } from "state/selections/Constants/SummaryType";
import { Grid, Cell } from "styled-css-grid";
import styled from "styled-components";
import FASelection from "./FASelection";
import Radio from "@ls/react-radio-switch";
import PivotDefinition from "state/pivots/PivotDefinition";
import { scaleTypeOptions } from "state/Constants/ScaleType";

const SubComplicationCell = styled(Cell)`
  position: relative;
  padding: 10px 0;
  border-radius: 3px;
  border: 1px solid #91929b;
  transition: border 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  &:hover {
    border: 1px solid #5e5f68;
  }
`;

const ScenarioTradeType = styled.div`
  display: grid;
  grid-template-columns: max-content min-content;
`;

const workSapceTradeTypes = [
  { label: "Default", value: "DEFAULT" },
  { label: "Buy", value: "BUY" },
  { label: "Sell", value: "SELL" },
];

const PivotsSelectorComplication = ({
  setDefinition,
  definition,
}: {
  setDefinition: any;
  definition: PivotDefinition;
}) => {
  const [filtersActive, setFiltersActive] = useState(false);

  const analyticOptions = useSelector(selectAnalyticValuesSelectionOptions);
  const fetchingAnalyticValues = useSelector(selectFetchingAnalyticValues);
  const groupingLevelOptions = useSelector(selectGroupingLevelOptions);
  const fetchingGroupingLevels = useSelector(selectFetchingGroupingLevels);

  const [
    setLevels,
    setAnalytics,
    setSummary,
    setPruneBuckets,
    setLookthru,
    setWorkspaceTradeType,
    setScaleType,
    setNewFilter,
    deleteFilter,
    setNewBeta,
    deleteBeta,
    setNewFa,
    deleteFa,
  ] = useMemo(() => {
    return [
      (value) => setDefinition(definition.setLevels(value)),
      (value) => setDefinition(definition.setAnalytics(value)),
      (value) => setDefinition(definition.setSummary(value)),
      (value) => setDefinition(definition.setPruneBuckets(value)),
      (value) => setDefinition(definition.setLookthru(value)),
      (value) => setDefinition(definition.setWorkspaceTradeType(value)),
      (value) => setDefinition(definition.setScaleType(value)),
      (newFilterDefinition) => {
        const { level, values } = newFilterDefinition;
        if (level && level.length > 0 && values && values.length > 0) {
          setDefinition(
            definition.setFilterDefinitions([
              ...(definition?.filterDefinitions ?? []),
              newFilterDefinition,
            ]),
          );
        }
      },
      (deletedFilterDefinition) => {
        setDefinition(
          definition.setFilterDefinitions(
            definition.filterDefinitions.filter(
              (def) => def.key !== deletedFilterDefinition.key,
            ),
          ),
        );
      },
      (idx, betaDefinition) => {
        const newBetaDefinitions = [...(definition?.betaDefinitions ?? [])];
        newBetaDefinitions[idx] = betaDefinition;
        setDefinition(definition.setBetaDefinitions(newBetaDefinitions));
      },
      (idx) => {
        const newBetaDefinitions = [...definition.betaDefinitions];
        newBetaDefinitions.splice(idx, 1);
        setDefinition(definition.setBetaDefinitions(newBetaDefinitions));
      },
      (idx, faDefinition) => {
        const newFaDefinitions = [...(definition?.faDefinitions ?? [])];
        newFaDefinitions[idx] = faDefinition;
        setDefinition(definition.setFaDefinitions(newFaDefinitions));
      },
      (idx) => {
        const newFaDefinitions = [...definition.faDefinitions];
        newFaDefinitions.splice(idx, 1);
        setDefinition(definition.setFaDefinitions(newFaDefinitions));
      },
    ];
  }, [definition, setDefinition]);

  return (
    <>
      <Grid columns={2} gap="4px">
        <Cell>
          <Combobox
            options={groupingLevelOptions}
            onChange={setLevels}
            isLoading={fetchingGroupingLevels}
            value={definition.levels}
            label="Pivot Level: "
            variation={0}
            gridHeight={250}
            size={Size.S}
          />
        </Cell>
        <Cell>
          <Combobox
            options={analyticOptions}
            onChange={setAnalytics}
            isLoading={fetchingAnalyticValues}
            value={definition.analytics}
            label="Analytic:"
            variation={0}
            gridHeight={250}
            size={Size.S}
          />
        </Cell>
        <Cell width={1}>
          <Combobox
            options={scaleTypeOptions}
            onChange={setScaleType}
            value={definition.scaleType}
            label="Type:"
            clearable={false}
            variation={0}
            size={"S"}
            gridHeight={175}
          />
        </Cell>
        <Cell>
          <Combobox
            options={summaryTypeOptionsLegacy}
            onChange={setSummary}
            value={definition.summary}
            label="Abs/Rel:"
            clearable={false}
            variation={0}
            size={"S"}
            gridHeight={100}
            disabled={definition.scaleType === "RATIO"}
          />
        </Cell>
        <Grid columns={2} gap="4px">
          <Cell middle>
            <Checkbox
              label={"Prune Buckets"}
              value={definition.pruneBuckets}
              onChange={setPruneBuckets}
            />
          </Cell>
          <Cell middle>
            <Checkbox
              label={"Lookthru"}
              value={definition.lookthru}
              onChange={setLookthru}
            />
          </Cell>
        </Grid>
        <Cell>
          <ScenarioTradeType>
            Scenario Trade Type:
            <Radio
              options={workSapceTradeTypes}
              onChange={setWorkspaceTradeType}
              value={definition.workspaceTradeType}
              horizontal
            />
          </ScenarioTradeType>
        </Cell>
        <SubComplicationCell width={2}>
          <FilterSummary
            filterDefinitions={definition.filterDefinitions}
            setFiltersActive={setFiltersActive}
            deleteFilter={deleteFilter}
          />
        </SubComplicationCell>
        <SubComplicationCell width={2}>
          <BetaSelection
            definition={definition}
            setNewBeta={setNewBeta}
            deleteBeta={deleteBeta}
          />
        </SubComplicationCell>
        <SubComplicationCell width={2}>
          <FASelection
            definition={definition}
            setNewFa={setNewFa}
            deleteFa={deleteFa}
          />
        </SubComplicationCell>
      </Grid>
      {filtersActive && (
        <FilterSelection
          definition={definition}
          setFiltersActive={setFiltersActive}
          setNewFilter={setNewFilter}
        />
      )}
    </>
  );
};

export default PivotsSelectorComplication;
